/**
 * Shared utilities for WrenchMode event log parsing and timer formatting.
 * Ported from mobile: /open-wrench-contractor-mobile-client/utils/wrenchModeEventUtils.js
 */
import moment from 'moment';
import { FINISHED_WORK_ORDER_DISPLAY_STATUSES } from '../constants/WorkOrderDisplayStatuses';

export interface WrenchModeEvent {
	id: number;
	eventType: string;
	createdAt: string;
	locationId: number;
	scheduleId: number;
	workOrderId?: number;
	serviceCallId?: number;
	checkoutStatus?: string;
	checkoutReason?: string;
	note?: string;
}

export interface WorkSession {
	events: WrenchModeEvent[];
	startTime: number;
	sessionMs: number;
}

export interface VisitSession {
	events: WrenchModeEvent[];
	driveEvents: WrenchModeEvent[];
	timerEvents: WrenchModeEvent[];
	workLogEvents: WrenchModeEvent[];
	driveTimeMs: number;
	workSessions: WorkSession[];
	workTimeMs: number;
	totalMs: number;
}

export interface ParsedLocationEvents {
	driveEvents: WrenchModeEvent[];
	driveTimeMs: number;
	timerEvents: WrenchModeEvent[];
	workLogEvents: WrenchModeEvent[];
	totalWorkMs: number;
	workSessions: WorkSession[];
	visitSessions: VisitSession[];
}

export const EMPTY_PARSED: ParsedLocationEvents = {
	driveEvents: [],
	driveTimeMs: 0,
	timerEvents: [],
	workLogEvents: [],
	totalWorkMs: 0,
	workSessions: [],
	visitSessions: [],
};

export const hasLocationEvents = (parsed: ParsedLocationEvents | undefined): boolean => {
	if (!parsed) return false;
	return (
		parsed.workLogEvents.length > 0 ||
		parsed.driveEvents.length > 0 ||
		parsed.timerEvents.length > 0
	);
};

/**
 * Collect all events from a hydrated schedule (location-level + schedule-level).
 */
export const collectScheduleEvents = (schedule: any): WrenchModeEvent[] => {
	const allEvents: WrenchModeEvent[] = [];
	const locations = schedule.locations || [];
	for (const loc of locations) {
		const locEvents = loc.events || [];
		allEvents.push.apply(allEvents, locEvents);
	}
	const scheduleEvents = schedule.events || [];
	allEvents.push.apply(allEvents, scheduleEvents);
	return allEvents;
};

const matchesTech = (sc, techEmail: string): boolean =>
	sc.leadTechnicianEmail === techEmail || (sc.additionalTechnicianEmails || []).includes(techEmail);

const sameDay = (value: string | number | null | undefined, date: string): boolean => {
	if (value === null || value === undefined || value === '') return false;
	// Accepts ISO strings or epoch ms (number or all-digit string).
	// `date` is "YYYY-MM-DD" in supplier-local TZ; moment uses the same TZ.
	const str = String(value);
	const m = /^\d+$/.test(str) ? moment(Number(str)) : moment(str);
	return m.isValid() && m.format('YYYY-MM-DD') === date;
};

const isWOStatusTerminal = (wo): boolean => {
	return (
		!!wo &&
		!!wo.displayStatus &&
		FINISHED_WORK_ORDER_DISPLAY_STATUSES.indexOf(wo.displayStatus) !== -1
	);
};

const techServiceCallsOnDate = (wo, techEmail: string, dateKey: string): any[] => {
	if (!wo || !techEmail || !dateKey) return [];
	return (wo.associatedServiceCalls || []).filter(
		(sc) =>
			matchesTech(sc, techEmail) &&
			sc.serviceScheduledAt &&
			moment(sc.serviceScheduledAt).format('YYYY-MM-DD') === dateKey
	);
};

const techCheckedInSCsOnDate = (wo, techEmail: string, dateKey: string): any[] => {
	return techServiceCallsOnDate(wo, techEmail, dateKey).filter((sc) => !!sc.checkInTime);
};

const hasPendingReturnVisit = (wo, techEmail: string, dateKey: string): boolean => {
	const scs = techServiceCallsOnDate(wo, techEmail, dateKey);
	const checkedIn = scs.filter((sc) => !!sc.checkInTime);
	if (checkedIn.length === 0) return false;
	const latestCheckout = checkedIn.reduce((max, sc) => {
		if (!sc.checkOutTime) return max;
		const t = new Date(sc.checkOutTime).getTime();
		return t > max ? t : max;
	}, 0);
	if (!latestCheckout) return false;
	return scs.some(
		(sc) =>
			!sc.checkInTime &&
			sc.serviceScheduledAt &&
			new Date(sc.serviceScheduledAt).getTime() > latestCheckout
	);
};

const isWOWrappedUpForTech = (wo, techEmail: string, dateKey: string): boolean => {
	if (isWOStatusTerminal(wo)) return true;
	const techSCs = techServiceCallsOnDate(wo, techEmail, dateKey);
	const checkedIn = techSCs.filter((sc) => !!sc.checkInTime);
	if (checkedIn.length === 0) return false;
	const lastSC = wo.lastServiceCall;
	if (!lastSC || !lastSC.checkOutTime) return false;
	return !hasPendingReturnVisit(wo, techEmail, dateKey);
};

const isWOStartedForTech = (wo, techEmail: string, dateKey: string): boolean => {
	if (isWOStatusTerminal(wo)) return false;
	const lastSC = wo.lastServiceCall;
	if (lastSC && lastSC.checkOutTime) return false;
	const checkedIn = techCheckedInSCsOnDate(wo, techEmail, dateKey);
	return checkedIn.some((sc) => !sc.checkOutTime);
};

export const countCompletedWorkOrders = (workOrders, techEmail: string, date: string): number => {
	let count = 0;
	for (const wo of workOrders) {
		if (!wo) continue;
		if (isWOWrappedUpForTech(wo, techEmail, date)) count++;
	}
	return count;
};

export const partitionWorkOrders = (
	workOrders: any[],
	techEmail: string,
	date: string
): { notStarted: any[]; started: any[]; ended: any[] } => {
	const notStarted: any[] = [];
	const started: any[] = [];
	const ended: any[] = [];
	for (const wo of workOrders) {
		if (!wo) continue;
		if (isWOWrappedUpForTech(wo, techEmail, date)) {
			ended.push(wo);
		} else if (isWOStartedForTech(wo, techEmail, date)) {
			started.push(wo);
		} else {
			notStarted.push(wo);
		}
	}
	return { notStarted, started, ended };
};

export const formatTimer = (ms: number): string => {
	if (ms <= 0) return '00:00:00';
	const totalSeconds = Math.floor(ms / 1000);
	const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
	const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
	const seconds = String(totalSeconds % 60).padStart(2, '0');
	return `${hours}:${minutes}:${seconds}`;
};

export const formatDuration = (ms: number): string => {
	if (ms <= 0) return '--';

	if (ms < 60000) {
		const seconds = Math.floor(ms / 1000);
		return `${seconds}s`;
	}

	const totalMinutes = Math.floor(ms / 60000);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;

	if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
	if (hours > 0) return `${hours}h`;

	return `${minutes}m`;
};

export const EVENT_LABELS: Record<string, string> = {
	began_trip: 'Began Trip',
	trip_paused: 'Trip Paused',
	trip_resumed: 'Trip Resumed',
	arrived: 'Arrived at Destination',
	trip_cancelled: 'Trip Cancelled',
	drive_note: 'Added Note',
	started_work: 'Started Work',
	ended_work: 'Finished Work',
	location_started: 'Started Work',
	location_paused: 'Paused Work',
	location_resumed: 'Resumed Work',
	location_ended: 'Ended Work at Location',
};

/**
 * Filter a flat events array to only events for a specific location.
 */
export const filterEventsByLocation = (
	events: WrenchModeEvent[],
	locationId: number
): WrenchModeEvent[] => {
	const locStr = String(locationId);
	return events.filter((e) => String(e.locationId) === locStr);
};

const DRIVE_TYPES = new Set([
	'began_trip',
	'trip_paused',
	'trip_resumed',
	'arrived',
	'trip_cancelled',
	'drive_note',
]);
const TIMER_TYPES = new Set([
	'location_started',
	'location_paused',
	'location_resumed',
	'location_ended',
]);

interface InternalVisit {
	events: WrenchModeEvent[];
	driveEvents: WrenchModeEvent[];
	timerEvents: WrenchModeEvent[];
	workLogEvents: WrenchModeEvent[];
	hasDrive: boolean;
	hasArrival: boolean;
}

const ms = (iso: string): number => new Date(iso).getTime();

/**
 * Sort by start, merge overlapping/adjacent intervals.
 * Returns wall-clock-correct interval list (concurrent overlaps collapse).
 */
const mergeOverlapping = (intervals: Array<[number, number]>): Array<[number, number]> => {
	if (intervals.length === 0) return [];
	const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
	const merged: Array<[number, number]> = [[sorted[0][0], sorted[0][1]]];
	for (let i = 1; i < sorted.length; i++) {
		const last = merged[merged.length - 1];
		const [s, e] = sorted[i];
		if (s <= last[1]) {
			last[1] = Math.max(last[1], e);
		} else {
			merged.push([s, e]);
		}
	}
	return merged;
};

const sumIntervals = (intervals: Array<[number, number]>): number =>
	intervals.reduce((sum, [s, e]) => sum + (e - s), 0);

const pointInsideAny = (t: number, intervals: Array<[number, number]>): boolean =>
	intervals.some(([s, e]) => t >= s && t <= e);

/**
 * Pair `started_work` → `ended_work` events by serviceCallId (preferred)
 * or workOrderId fallback. Pairs are emitted in chronological order of `started_work`.
 * Unmatched `started_work` events (still open) are not returned.
 */
const pairStartedEnded = (
	events: WrenchModeEvent[]
): Array<{ started: WrenchModeEvent; ended: WrenchModeEvent }> => {
	const sorted = events
		.filter((e) => e.eventType === 'started_work' || e.eventType === 'ended_work')
		.sort((a, b) => ms(a.createdAt) - ms(b.createdAt));

	const keyOf = (evt: WrenchModeEvent): string =>
		evt.serviceCallId != null ? `sc_${evt.serviceCallId}` : `wo_${evt.workOrderId}`;

	const open = new Map<string, WrenchModeEvent>();
	const pairs: Array<{ started: WrenchModeEvent; ended: WrenchModeEvent }> = [];
	for (const evt of sorted) {
		const k = keyOf(evt);
		if (evt.eventType === 'started_work') {
			open.set(k, evt);
		} else {
			const start = open.get(k);
			if (start) {
				pairs.push({ started: start, ended: evt });
				open.delete(k);
			}
		}
	}
	return pairs;
};

/**
 * Parse eventLog for a location into structured drive/work data.
 *
 * Hybrid rules :
 *   - WM-tracked work: location_started → location_ended (with pauses).
 *   - Non-WM work: started_work → ended_work pairs whose start is NOT inside
 *     any WM interval. Each pair becomes its own VisitSession with empty
 *     drive/timer arrays.
 *   - totalWorkMs = sum of merge-overlapping(WM intervals + non-WM intervals).
 *     This collapses concurrent overlap to wall-clock and is the
 *     single source of truth for "work time" in the dashboard.
 *   - Drive time stays WM-only (began_trip → arrived, minus trip pauses).
 *     Gap-derived drive is computed at the row-aggregation layer because it
 *     needs the cross-location SC list.
 */
export const parseLocationEvents = (events: WrenchModeEvent[]): ParsedLocationEvents => {
	const driveEvents: WrenchModeEvent[] = [];
	const workLogEvents: WrenchModeEvent[] = [];
	const timerEvents: WrenchModeEvent[] = [];

	for (const evt of events) {
		switch (evt.eventType) {
			case 'began_trip':
			case 'trip_paused':
			case 'trip_resumed':
			case 'arrived':
			case 'trip_cancelled':
			case 'drive_note':
				driveEvents.push(evt);
				break;
			case 'started_work':
			case 'ended_work':
				workLogEvents.push(evt);
				break;
			case 'location_started':
			case 'location_paused':
			case 'location_resumed':
			case 'location_ended':
				timerEvents.push(evt);
				break;
		}
	}

	const allEvents = [...events].sort((a, b) => ms(a.createdAt) - ms(b.createdAt));

	// ─── WM work intervals from location_* (used for "is event inside WM?" check) ──
	const wmWorkIntervals: Array<[number, number]> = [];
	{
		const sortedTimer = [...timerEvents].sort((a, b) => ms(a.createdAt) - ms(b.createdAt));
		let openStart: number | null = null;
		for (const evt of sortedTimer) {
			const t = ms(evt.createdAt);
			if (evt.eventType === 'location_started' || evt.eventType === 'location_resumed') {
				if (openStart === null) openStart = t;
			} else if (evt.eventType === 'location_paused' || evt.eventType === 'location_ended') {
				if (openStart !== null) {
					wmWorkIntervals.push([openStart, t]);
					openStart = null;
				}
			}
		}
		// Open WM session (no location_ended yet) contributes 0 to totals — same as existing behavior.
	}

	// ─── Group drive + timer events into visits ───────────────────────────────────
	// Work events are excluded from the grouper and injected afterward so that
	// a started_work before location_started doesn't create a phantom visit.
	const wmGroupingEvents = allEvents.filter(
		(evt) => evt.eventType !== 'started_work' && evt.eventType !== 'ended_work'
	);

	const internalVisits: InternalVisit[] = [];
	let currentVisit: InternalVisit | null = null;

	for (const evt of wmGroupingEvents) {
		const isVisitStart =
			evt.eventType === 'began_trip' ||
			(evt.eventType === 'arrived' && (!currentVisit || !currentVisit.hasDrive)) ||
			(evt.eventType === 'location_started' &&
				(!currentVisit || (!currentVisit.hasDrive && !currentVisit.hasArrival)));

		if (isVisitStart) {
			if (currentVisit) internalVisits.push(currentVisit);
			currentVisit = {
				events: [],
				driveEvents: [],
				timerEvents: [],
				workLogEvents: [],
				hasDrive: evt.eventType === 'began_trip',
				hasArrival: evt.eventType === 'arrived',
			};
		}

		if (!currentVisit) {
			currentVisit = {
				events: [],
				driveEvents: [],
				timerEvents: [],
				workLogEvents: [],
				hasDrive: false,
				hasArrival: false,
			};
		}

		currentVisit.events.push(evt);
		if (DRIVE_TYPES.has(evt.eventType)) {
			currentVisit.driveEvents.push(evt);
			if (evt.eventType === 'began_trip') currentVisit.hasDrive = true;
			if (evt.eventType === 'arrived') currentVisit.hasArrival = true;
		} else if (TIMER_TYPES.has(evt.eventType)) {
			currentVisit.timerEvents.push(evt);
		}
	}
	if (currentVisit) internalVisits.push(currentVisit);

	// ─── Inject work events into visits by nearest time range ────────────────────
	// When WM sessions exist, all work events belong to them — boundaries stretch
	// to fit. When no WM sessions exist, work events become standalone visits below.
	const hasWmSessions = wmWorkIntervals.length > 0;
	const eventsToInject = hasWmSessions ? workLogEvents : [];
	for (const woEvt of eventsToInject) {
		const woTime = ms(woEvt.createdAt);
		let bestVisit: InternalVisit | null = null;
		let bestDist = Infinity;
		for (const visit of internalVisits) {
			if (visit.events.length === 0) continue;
			const visitStart = ms(visit.events[0].createdAt);
			const visitEnd = ms(visit.events[visit.events.length - 1].createdAt);
			if (woTime >= visitStart && woTime <= visitEnd) {
				bestVisit = visit;
				break;
			}
			const dist = Math.min(Math.abs(woTime - visitStart), Math.abs(woTime - visitEnd));
			if (dist < bestDist) {
				bestDist = dist;
				bestVisit = visit;
			}
		}
		if (bestVisit) {
			bestVisit.events.push(woEvt);
			bestVisit.workLogEvents.push(woEvt);
		}
	}

	// ─── Compute per-WM-visit drive + work sessions (existing math) ────────────────
	let driveTimeMsTotal = 0;
	const visitSessions: VisitSession[] = [];

	for (const visit of internalVisits) {
		// Drive time
		const beganTrip = visit.driveEvents.find((e) => e.eventType === 'began_trip');
		const arrived = visit.driveEvents.find((e) => e.eventType === 'arrived');
		const tripCancelled = visit.driveEvents.find((e) => e.eventType === 'trip_cancelled');
		const driveEndEvent = arrived || tripCancelled;
		let visitDriveTimeMs = 0;
		if (beganTrip && driveEndEvent) {
			const totalMs = ms(driveEndEvent.createdAt) - ms(beganTrip.createdAt);
			let tripPausedMs = 0;
			let lastTripPause: number | null = null;
			for (const evt of visit.driveEvents) {
				if (evt.eventType === 'trip_paused') {
					lastTripPause = ms(evt.createdAt);
				} else if (
					(evt.eventType === 'trip_resumed' ||
						evt.eventType === 'arrived' ||
						evt.eventType === 'trip_cancelled') &&
					lastTripPause
				) {
					tripPausedMs += ms(evt.createdAt) - lastTripPause;
					lastTripPause = null;
				}
			}
			visitDriveTimeMs = totalMs - tripPausedMs;
		}
		driveTimeMsTotal += visitDriveTimeMs;

		// Work sessions: location_started → location_ended (with pauses)
		const workSessions: WorkSession[] = [];
		let curSession: { events: WrenchModeEvent[]; startTime: number } | null = null;
		for (const evt of visit.timerEvents) {
			if (evt.eventType === 'location_started') {
				curSession = { events: [evt], startTime: ms(evt.createdAt) };
			} else if (curSession) {
				curSession.events.push(evt);
				if (evt.eventType === 'location_ended') {
					const endTime = ms(evt.createdAt);
					let sessionPausedMs = 0;
					let lp: number | null = null;
					for (const se of curSession.events) {
						const st = ms(se.createdAt);
						if (se.eventType === 'location_paused') lp = st;
						else if (
							(se.eventType === 'location_resumed' || se.eventType === 'location_ended') &&
							lp
						) {
							sessionPausedMs += st - lp;
							lp = null;
						}
					}
					workSessions.push({
						events: curSession.events,
						startTime: curSession.startTime,
						sessionMs: endTime - curSession.startTime - sessionPausedMs,
					});
					curSession = null;
				}
			}
		}
		if (curSession) {
			workSessions.push({
				events: curSession.events,
				startTime: curSession.startTime,
				sessionMs: 0,
			});
		}

		// Inject workLogEvents into matching work sessions for display.
		// If a work event falls before the first session (e.g. a revised
		// check-in time earlier than location_started), attach it to the
		// earliest session so it is not orphaned from the timeline.
		for (const woEvt of visit.workLogEvents) {
			const woTime = ms(woEvt.createdAt);
			let injected = false;
			for (const session of workSessions) {
				const endEvt = session.events.find((e) => e.eventType === 'location_ended');
				const sessionEnd = endEvt ? ms(endEvt.createdAt) : Infinity;
				if (woTime >= session.startTime && woTime <= sessionEnd) {
					session.events.push(woEvt);
					injected = true;
					break;
				}
			}
			if (!injected && workSessions.length > 0) {
				if (woTime < workSessions[0].startTime) {
					workSessions[0].sessionMs += workSessions[0].startTime - woTime;
					workSessions[0].startTime = woTime;
					workSessions[0].events.push(woEvt);
				} else {
					const lastSession = workSessions[workSessions.length - 1];
					const lastSessionEnd = lastSession.events.reduce((max, e) => {
						const t = ms(e.createdAt);
						return t > max ? t : max;
					}, lastSession.startTime);
					if (woTime > lastSessionEnd) {
						lastSession.sessionMs += woTime - lastSessionEnd;
					}
					lastSession.events.push(woEvt);
				}
			}
		}
		for (const session of workSessions) {
			session.events.sort((a, b) => ms(a.createdAt) - ms(b.createdAt));
		}

		const visitWorkTimeMs = workSessions.reduce((sum, s) => sum + (s.sessionMs || 0), 0);

		visitSessions.push({
			events: visit.events,
			driveEvents: visit.driveEvents,
			timerEvents: visit.timerEvents,
			workLogEvents: visit.workLogEvents,
			driveTimeMs: visitDriveTimeMs,
			workSessions,
			workTimeMs: visitWorkTimeMs,
			totalMs: visitDriveTimeMs + visitWorkTimeMs,
		});
	}

	// ─── Synthesize visits for standalone started_work/ended_work pairs ───────────
	// Only when no WM sessions exist (otherwise all work events were injected above).
	const standalonePairs = hasWmSessions ? [] : pairStartedEnded(workLogEvents);

	for (const { started, ended } of standalonePairs) {
		const startMs = ms(started.createdAt);
		const endMs = ms(ended.createdAt);
		const sessionMs = Math.max(0, endMs - startMs);
		const session: WorkSession = {
			events: [started, ended],
			startTime: startMs,
			sessionMs,
		};
		visitSessions.push({
			events: [started, ended],
			driveEvents: [],
			timerEvents: [],
			workLogEvents: [started, ended],
			driveTimeMs: 0,
			workSessions: [session],
			workTimeMs: sessionMs,
			totalMs: sessionMs,
		});
	}

	// Sort visits chronologically by their first event
	visitSessions.sort((a, b) => {
		const aStart = a.events.length > 0 ? ms(a.events[0].createdAt) : 0;
		const bStart = b.events.length > 0 ? ms(b.events[0].createdAt) : 0;
		return aStart - bStart;
	});

	// totalWorkMs = sum of all visit work times (sessions already include extensions)
	const totalWorkMs = visitSessions.reduce((sum, v) => sum + v.workTimeMs, 0);

	return {
		driveEvents,
		driveTimeMs: driveTimeMsTotal,
		timerEvents,
		workLogEvents,
		totalWorkMs,
		workSessions: visitSessions.reduce((acc: WorkSession[], v) => acc.concat(v.workSessions), []),
		visitSessions,
	};
};
